import {
    createFileRoute,
    stripSearchParams,
    useRouter,
} from '@tanstack/react-router';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { mergeQuery } from '~/util/queries/staff/merges';
import { userProfileQuery } from '~/util/queries/public/profiles';
import { MergeRequestUserSummary } from '../../-MergeRequestUserSummary';
import { Button } from '~/primitives/button';
import {
    acceptMergeRequestMutation,
    claimMergeRequestMutation,
    rejectMergeRequestMutation,
    unclaimMergeRequestMutation,
} from '~/util/mutations/staff/merges';
import {
    defaultMergeRequestParams,
    mergeRequestParamsSchema,
} from './-SearchParams';
import { Switch } from '~/primitives/switch';

export const Route = createFileRoute(
    '/_authenticated/staff/_mergesLayout/merges/$requestid',
)({
    validateSearch: mergeRequestParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultMergeRequestParams)],
    },
    loader: async ({ context: { queryClient }, params: { requestid } }) => {
        const request = await queryClient.fetchQuery(
            mergeQuery({
                requestid,
            }),
        );
        const [
            primary_user_classic,
            primary_user_platformer,
            secondary_user_classic,
            secondary_user_platformer,
        ] = await Promise.all([
            queryClient.fetchQuery(
                userProfileQuery({
                    list: 'classic',
                    userid: request.primary_user.id,
                }),
            ),
            queryClient.fetchQuery(
                userProfileQuery({
                    list: 'platformer',
                    userid: request.primary_user.id,
                }),
            ),
            queryClient.fetchQuery(
                userProfileQuery({
                    list: 'classic',
                    userid: request.secondary_user.id,
                }),
            ),
            queryClient.fetchQuery(
                userProfileQuery({
                    list: 'platformer',
                    userid: request.secondary_user.id,
                }),
            ),
        ]);
        return {
            request,
            primary_user_classic,
            primary_user_platformer,
            secondary_user_classic,
            secondary_user_platformer,
        };
    },
    component: MergeRequest,
});

function MergeRequest() {
    const {
        request,
        primary_user_classic,
        primary_user_platformer,
        secondary_user_classic,
        secondary_user_platformer,
    } = Route.useLoaderData({
        select: ({
            request,
            primary_user_classic,
            primary_user_platformer,
            secondary_user_classic,
            secondary_user_platformer,
        }) => ({
            request,
            primary_user_classic,
            primary_user_platformer,
            secondary_user_classic,
            secondary_user_platformer,
        }),
    });

    const auto_claim = Route.useSearch({
        select: (search) => search.auto_claim,
    });

    const navigate = Route.useNavigate();
    const router = useRouter();

    const claimMergeMutation = useApiMutation({
        ...claimMergeRequestMutation(),
        onSuccess: ({ data }) => {
            navigate({
                to: '/staff/merges/$requestid',
                params: { requestid: data.id },
                search: (prev) => prev,
            });
        },
    });

    const handleNext = async () => {
        if (auto_claim) {
            claimMergeMutation.mutate();
        } else {
            await navigate({ to: '/staff/merges', search: (prev) => prev });
            await router.invalidate({ sync: true });
        }
    };

    const acceptMergeMutation = useApiMutation({
        ...acceptMergeRequestMutation(),
        onSuccess: handleNext,
    });
    const denyMergeMutation = useApiMutation({
        ...rejectMergeRequestMutation(),
        onSuccess: handleNext,
    });
    const unclaimMergeMutation = useApiMutation(unclaimMergeRequestMutation());

    return (
        <>
            <div className="flex mr-auto ml-auto lg:mr-0 gap-2 my-2 w-fit border p-2 rounded-lg">
                <Switch
                    checked={auto_claim}
                    onCheckedChange={(checked) => {
                        navigate({
                            search: (prev) => ({
                                ...prev,
                                auto_claim: checked,
                            }),
                            replace: true,
                        });
                    }}
                />
                Auto-claim
            </div>
            <div className="flex flex-col lg:flex-row gap-4 w-full">
                <div className="w-full border rounded-lg p-4 bg-background-darker">
                    <h2>Primary User</h2>
                    <p className="mb-2">
                        This user will be kept after the merge
                    </p>
                    <MergeRequestUserSummary
                        classic={primary_user_classic}
                        platformer={primary_user_platformer}
                    />
                </div>
                <div className="w-full border rounded-lg p-4 bg-background-darker">
                    <h2>Secondary User</h2>
                    <p className="mb-2">
                        This user will have all its data merged into the primary
                        user, and will then be deleted.
                    </p>
                    <MergeRequestUserSummary
                        classic={secondary_user_classic}
                        platformer={secondary_user_platformer}
                    />
                </div>
            </div>
            <div className="p-4 flex gap-4 justify-center">
                <Button
                    className="bg-confirm hover:bg-confirm w-lg"
                    onClick={() =>
                        acceptMergeMutation.mutate({ mergeid: request.id })
                    }
                >
                    Accept
                </Button>
                {!request.is_rejected && (
                    <Button
                        className="bg-destructive hover:bg-destructive w-lg"
                        onClick={() =>
                            denyMergeMutation.mutate({ mergeid: request.id })
                        }
                    >
                        Deny
                    </Button>
                )}
                {request.is_claimed && (
                    <Button
                        className="bg-pending hover:bg-pending w-lg"
                        onClick={() =>
                            unclaimMergeMutation.mutate({ mergeid: request.id })
                        }
                    >
                        Unclaim
                    </Button>
                )}
            </div>
        </>
    );
}
